import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ShieldCheck, Lock, FileSignature, Send, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.7 1.22 9.2 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

export default function Landing() {
  const { user, loading, login, register } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("login");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", business_name: "" });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref) localStorage.setItem("authvault_ref", ref);
    const invite = params.get("invite");
    if (invite) {
      localStorage.setItem("authvault_invite", invite);
      setTab("register");
      toast.info("You've been invited to a team — sign in or create an account to accept.");
    }
  }, []);

  useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [user, loading, navigate]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (tab === "login") {
        await login(form.email, form.password);
      } else {
        await register(form);
      }
      toast.success("Welcome to AuthVault");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left — brand / value */}
      <div className="relative hidden lg:flex flex-col justify-between bg-[#0F172A] text-white p-12 overflow-hidden">
        <div className="absolute inset-0 grid-backdrop opacity-40" />
        <div className="relative">
          <div className="flex items-center gap-2.5">
            <span className="h-10 w-10 rounded-xl bg-white/10 flex items-center justify-center">
              <ShieldCheck size={22} className="text-[#38BDF8]" />
            </span>
            <span className="font-extrabold text-xl tracking-tight">AuthVault</span>
          </div>
        </div>

        <div className="relative max-w-md">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#38BDF8] mb-4">
            Digital Card Authorizations
          </p>
          <h1 className="text-4xl xl:text-5xl font-extrabold leading-[1.05] tracking-tight">
            Collect signed card authorizations in minutes — not paper.
          </h1>
          <p className="mt-5 text-slate-300 leading-relaxed">
            Build a form, send a secure link, capture a legally-binding e-signature, and store every
            authorization in one place. Free for small &amp; medium businesses.
          </p>
          <div className="mt-8 space-y-3">
            {[
              { icon: FileSignature, t: "Draw or type e-signature with full audit trail" },
              { icon: Send, t: "Share via secure link or email in one click" },
              { icon: Lock, t: "Only the last 4 card digits are ever stored" },
            ].map((f, i) => (
              <div key={i} className="flex items-center gap-3 text-sm text-slate-200">
                <span className="h-8 w-8 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
                  <f.icon size={16} className="text-[#38BDF8]" />
                </span>
                {f.t}
              </div>
            ))}
          </div>
        </div>

        <div className="relative flex items-center gap-2 text-xs text-slate-400">
          <Lock size={13} /> PCI-minded · 256-bit SSL · Signature audit certificates
        </div>
      </div>

      {/* Right — auth */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-[#F8FAFC]">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="w-full max-w-sm"
        >
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <span className="h-9 w-9 rounded-lg bg-[#0F172A] flex items-center justify-center">
              <ShieldCheck size={18} className="text-[#38BDF8]" />
            </span>
            <span className="font-extrabold text-lg tracking-tight text-[#0F172A]">AuthVault</span>
          </div>

          <h2 className="text-2xl font-bold tracking-tight text-[#0F172A]">
            {tab === "login" ? "Welcome back" : "Create your free account"}
          </h2>
          <p className="text-sm text-[#64748B] mt-1">
            {tab === "login" ? "Sign in to manage your authorizations." : "Start collecting authorizations today."}
          </p>

          <div className="mt-6 grid grid-cols-2 gap-1 rounded-lg bg-slate-100 p-1">
            {["login", "register"].map((t) => (
              <button
                key={t}
                data-testid={`auth-tab-${t}`}
                onClick={() => setTab(t)}
                className={`rounded-md py-2 text-sm font-semibold transition-colors ${
                  tab === t ? "bg-white text-[#0F172A] shadow-sm" : "text-[#64748B]"
                }`}
              >
                {t === "login" ? "Sign in" : "Sign up"}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="mt-5 space-y-3.5">
            {tab === "register" && (
              <>
                <Field label="Full name" testid="register-name-input" value={form.name} onChange={set("name")} required />
                <Field label="Business name" testid="register-business-input" value={form.business_name} onChange={set("business_name")} placeholder="Optional" />
              </>
            )}
            <Field label="Email" type="email" testid="auth-email-input" value={form.email} onChange={set("email")} required />
            <Field label="Password" type="password" testid="auth-password-input" value={form.password} onChange={set("password")} required />

            <button
              type="submit"
              data-testid="auth-submit-btn"
              disabled={busy}
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[#2563EB] py-2.5 text-sm font-semibold text-white hover:bg-[#1D4ED8] transition-all active:scale-[0.99] disabled:opacity-60"
            >
              {busy && <Loader2 size={16} className="animate-spin" />}
              {tab === "login" ? "Sign in" : "Create account"}
            </button>
          </form>

          <div className="flex items-center gap-3 my-5">
            <span className="h-px flex-1 bg-[#E2E8F0]" />
            <span className="text-xs text-[#94A3B8]">or</span>
            <span className="h-px flex-1 bg-[#E2E8F0]" />
          </div>

          <button
            data-testid="google-login-btn"
            onClick={googleLogin}
            className="w-full inline-flex items-center justify-center gap-2.5 rounded-lg border border-[#E2E8F0] bg-white py-2.5 text-sm font-semibold text-[#0F172A] hover:bg-slate-50 transition-colors"
          >
            <GoogleIcon /> Continue with Google
          </button>
        </motion.div>
      </div>
    </div>
  );
}

const Field = ({ label, testid, type = "text", ...rest }) => (
  <label className="block">
    <span className="text-xs font-semibold uppercase tracking-wider text-[#64748B]">{label}</span>
    <input
      type={type}
      data-testid={testid}
      className="mt-1.5 w-full rounded-lg border border-[#E2E8F0] px-3 py-2.5 text-sm outline-none focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/10 transition-all"
      {...rest}
    />
  </label>
);
